import { useNavigate } from '@tanstack/react-router';
import { KeyRound, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { useState, type SyntheticEvent, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import { PaymentFlowMark, WebsiteMark } from '@/components/layout/brand';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { ThemeToggle } from '@/components/layout/theme';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/feedback';
import { Code, Input, Label } from '@/components/ui/primitives';
import { usingMocks } from '@/lib/api/http';
import { OPERATOR_TOTP_ENABLED } from '@/lib/api/ops-session';
import { SEED_ACCOUNTS } from '@/lib/dev/seed-accounts';
import { OPERATORS } from '@/mocks/people';

/**
 * The operator login screen.
 *
 * Operators only: the customer plane was removed. Customers reach their cashier through the
 * participating website's own app, which authenticates them against its own BFF — this console
 * never held their credentials for any reason other than convenience, and mixing the two on one
 * form meant a PaymentFlow operator password could be posted at the participant's website.
 *
 * `POST /ops/v1/session` takes password *and* TOTP in a single request: SAD §11 makes the second
 * factor mandatory for every operator, every time, and the API deliberately offers no way to check
 * a password without one — a "password OK, now show me a code" step would confirm which usernames
 * are real operators. (While `OPERATOR_TOTP_ENABLED` is off, the code step is skipped and the
 * request carries no `totp` — the form below is kept for when it comes back.)
 *
 * The form still collects the two in two steps, because typing a password and reading a code off a
 * phone are different motions. Step one is *local*: nothing is sent until the code is in hand.
 */
export function LoginPage(): ReactNode {
  const { t } = useTranslation();
  const { signInOperator } = useAuth();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const goToArea = (area: 'customer' | 'operator'): void => {
    void navigate({ to: area === 'operator' ? '/ops/dashboard' : '/app/deposit', replace: true });
  };

  const onPassword = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);

    // Advance to the code field without touching the network. There is nothing useful to ask the
    // API yet, and asking would leak which usernames are real operators.
    if (OPERATOR_TOTP_ENABLED) {
      setNeedsCode(true);
      return;
    }

    // With the second factor switched off, the password alone opens the session and the code step
    // is never shown.
    setBusy(true);
    signInOperator({ username: identifier.trim(), password })
      .then((session) => {
        goToArea(session.area);
      })
      .catch((cause: unknown) => {
        setError(cause);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const onTotp = (event: SyntheticEvent): void => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    signInOperator({ username: identifier.trim(), password, totp: code })
      .then((session) => {
        goToArea(session.area);
      })
      .catch((cause: unknown) => {
        setError(cause);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_minmax(0,480px)]">
      {/* Explanatory panel */}
      <aside
        aria-label={t('login.hero.ariaLabel')}
        className="relative hidden flex-col justify-between overflow-hidden bg-sidebar p-10 text-sidebar-foreground lg:flex"
      >
        <div className="absolute inset-0 bg-grid opacity-20" aria-hidden="true" />
        <div className="relative flex items-center gap-2.5">
          <PaymentFlowMark size={32} />
          <span className="text-base font-semibold tracking-tight">PaymentFlow</span>
        </div>

        <div className="relative max-w-lg space-y-6">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            {t('login.hero.title')}
          </h2>
          <p className="text-sm leading-relaxed text-sidebar-muted">{t('login.hero.description')}</p>

          <ul className="space-y-4 text-sm">
            {[
              {
                icon: WebsiteMark,
                title: t('login.hero.features.dualArea.title'),
                body: t('login.hero.features.dualArea.body'),
              },
              {
                icon: ShieldCheck,
                title: t('login.hero.features.secondFactor.title'),
                body: t('login.hero.features.secondFactor.body'),
              },
              {
                icon: Sparkles,
                title: t('login.hero.features.evidence.title'),
                body: t('login.hero.features.evidence.body'),
              },
            ].map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3">
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="size-4 text-sidebar-accent" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-medium">{title}</span>
                  <span className="block text-sidebar-muted">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-2xs text-sidebar-muted">{t('login.hero.footer')}</p>
      </aside>

      {/* Form */}
      <main className="relative flex flex-col justify-center gap-8 px-6 py-10 sm:px-10">
        <div className="flex items-center justify-between lg:hidden">
          <span className="flex items-center gap-2.5">
            <PaymentFlowMark size={28} />
            <span className="text-sm font-semibold">PaymentFlow</span>
          </span>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
        <div className="absolute right-6 top-6 hidden items-center gap-2 lg:flex">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>

        <div className="mx-auto w-full max-w-sm space-y-6">
          {!needsCode ? (
            <>
              <div className="space-y-1.5">
                <h1 className="text-2xl font-semibold tracking-tight">{t('login.form.title')}</h1>
                <p className="text-sm text-muted-foreground">
                  {t('login.form.subtitleOperator')}
                </p>
              </div>

              <form className="space-y-4" onSubmit={onPassword} noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="identifier">{t('login.form.usernameLabel')}</Label>
                  <Input
                    id="identifier"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={identifier}
                    placeholder={t('login.form.usernamePlaceholder')}
                    onChange={(event) => {
                      setIdentifier(event.target.value);
                    }}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">{t('login.form.passwordLabel')}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                  />
                </div>

                {error !== null ? <ErrorState error={error} /> : null}

                <Button type="submit" className="w-full" loading={busy}>
                  <Lock aria-hidden="true" />
                  {/* "Continue" promises a second step; with the code step off it IS the sign-in. */}
                  {OPERATOR_TOTP_ENABLED
                    ? t('login.form.submitOperator')
                    : t('login.form.submitCustomer')}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <h1 className="text-2xl font-semibold tracking-tight">{t('login.totp.title')}</h1>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t('login.totp.description')}
                </p>
              </div>

              <form className="space-y-4" onSubmit={onTotp} noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="totp">{t('login.totp.codeLabel')}</Label>
                  <Input
                    id="totp"
                    name="totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    placeholder={t('login.totp.codePlaceholder')}
                    className="tnum text-center text-lg tracking-[0.5em]"
                    onChange={(event) => {
                      setCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                    }}
                  />
                  {usingMocks() ? (
                    <p className="text-2xs text-muted-foreground">
                      <Trans
                        i18nKey="login.totp.mockHint"
                        values={{ code: '000000' }}
                        components={{ code: <Code /> }}
                      />
                    </p>
                  ) : null}
                </div>

                {error !== null ? <ErrorState error={error} /> : null}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNeedsCode(false);
                      setCode('');
                      setError(null);
                    }}
                  >
                    {t('login.totp.back')}
                  </Button>
                  <Button type="submit" className="flex-1" loading={busy}>
                    <KeyRound aria-hidden="true" />
                    {t('login.totp.verify')}
                  </Button>
                </div>
              </form>
            </>
          )}

          <DemoIdentities
            onPick={(pickUsername, pickPassword) => {
              setIdentifier(pickUsername);
              setPassword(pickPassword);
              setNeedsCode(false);
              setCode('');
              setError(null);
            }}
          />
        </div>
      </main>
    </div>
  );
}

/** One row of the identity list — name, username, an optional hint, and a role badge. */
interface DemoIdentity {
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly password: string;
  readonly hint?: string;
}

/**
 * The available test accounts, offered as one-click fills so every role is reachable in a demo.
 *
 * Shown in two development situations, hidden everywhere else:
 *
 * - **On mocks** (`usingMocks()`): the MSW people — those are the only identities that exist in the
 *   mock world, and their password is anything ≥ 4 characters.
 * - **Against the real seeded dev backend** (`import.meta.env.DEV`, MSW off): the accounts
 *   `pnpm seed` actually writes (`lib/dev/seed-accounts.ts`), so the list matches the running
 *   database rather than the mock fixtures.
 *
 * `import.meta.env.DEV` is the literal `false` in any production build, so this whole component —
 * and the seeded account list it imports — is dead code the bundler drops. A production console
 * never ships a roster of accounts on its login screen.
 *
 * The DEV check must come *first* and stand *alone*: the previous form (`!onMocks && !DEV`)
 * depended on `usingMocks()` also being a build-time constant, and when a 2026-08-05 build was
 * run without `VITE_USE_MSW=false`, that constant flipped to `true` and this panel rendered on
 * the production login page. Now no environment variable can resurrect it in a prod build.
 */
function DemoIdentities({
  onPick,
}: {
  onPick: (username: string, password: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  // Never in a production build, full stop. Vite replaces `import.meta.env.DEV` with `false` at
  // build time, so everything past this line is unreachable and tree-shaken out of prod bundles.
  if (!import.meta.env.DEV) return null;
  const onMocks = usingMocks();

  // Operators only: this screen no longer signs customers in, so a customer row here would fill
  // the form with an identity the API cannot accept.
  const identities: readonly DemoIdentity[] = onMocks
    ? OPERATORS.map((o) => ({
        email: o.username,
        role: String(o.role),
        name: o.displayName,
        password: 'demo-password',
      }))
    : SEED_ACCOUNTS.filter((a) => a.role !== 'CUSTOMER');

  return (
    <div className="space-y-2 rounded-lg border border-dashed border-border p-4">
      <p className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
        {onMocks ? t('login.demo.mockTitle') : t('login.demo.seedTitle')}
      </p>
      <ul className="space-y-1">
        {identities.map((identity) => (
          <li key={identity.email}>
            <button
              type="button"
              onClick={() => {
                onPick(identity.email, identity.password);
              }}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium text-foreground">{identity.name}</span>
                <span className="block truncate text-muted-foreground">{identity.email}</span>
                {'hint' in identity && identity.hint !== undefined ? (
                  <span className="block truncate text-2xs text-muted-foreground">
                    {identity.hint}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-2xs font-medium text-muted-foreground">
                {identity.role}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-2xs leading-relaxed text-muted-foreground">
        {onMocks ? t('login.demo.mockFootnote') : t('login.demo.seedFootnote')}
      </p>
    </div>
  );
}
